// zerlege.ts — das gemeinsame Textformat aller Bausteine.
//
// Warum es das gibt: In Webflow eine Tabelle mit 5 Zeilen und 4 Spalten zu
// pflegen heisst, 20 Zellen einzeln anzuklicken. Stattdessen steht der Inhalt
// als Rohtext in einem Embed, und der Baustein zerlegt ihn selbst.
//
// Das ist kein Notbehelf, sondern der eigentliche Gewinn: eine Tabelle wird
// einkopiert statt Zelle fuer Zelle angeklickt. Und weil Mariann ihre Entwuerfe
// von Claude als Markdown bekommt, versteht der Parser bewusst auch echte
// Markdown-Tabellen samt Trennzeile — dann ist Einfuegen wirklich Einfuegen.
//
// Diese Datei ist bewusst frei von DOM und Framework: sie war zuerst fuer
// Webflow Code Components geschrieben und laeuft unveraendert weiter, nachdem
// der Weg auf das data-module-System gewechselt ist. Nur deshalb liess sich
// dieser Teil samt seiner Tests eins zu eins uebernehmen.
//
// Das Format, einmal fuer alle Bausteine:
//
//   Spalte A | Spalte B | Spalte C     eine Zeile je Eintrag, Felder mit |
//   ## Kategorie                        beginnt eine neue Gruppe
//   | a | b |                           fuehrende/schliessende | sind erlaubt
//   |---|---|                           Markdown-Trennzeile wird uebersprungen
//   Text mit \| Zeichen                 \| ergibt ein literales |

/** Punkt, geschuetztes und schmales Leerzeichen — alles, was Tausender trennt. */
const TAUSENDERTRENNER = '[.\\u00a0\\u202f ]';

export type QuelleMitEinstellungen = {
  /** Kleingeschriebene Namen ohne `data-`-Praefix, z. B. { titel: "..." }. */
  einstellungen: Record<string, string>;
  /** Die Quelle ohne die @-Zeilen. */
  inhalt: string;
};

/**
 * Zieht Einstellungszeilen aus dem Rohtext: `@titel: Rechenbeispiel`.
 *
 * Warum im Text und nicht nur als data-Attribut am Wrapper: Die Wrapper sind
 * Webflow-Komponenten, und deren Attribute lassen sich nicht je Instanz
 * aendern — der Rohtext schon. Mit den @-Zeilen steuert ein einziges Textfeld
 * Inhalt UND Einstellungen. Attribute am Wrapper bleiben als Standard gueltig;
 * eine @-Zeile gewinnt, weil sie die instanznahe Angabe ist.
 */
export function zerlegeEinstellungen(quelle: string | null | undefined): QuelleMitEinstellungen {
  if (!quelle) return { einstellungen: {}, inhalt: '' };

  const einstellungen: Record<string, string> = {};
  const zeilen: string[] = [];

  for (const roh of quelle.split(/\r?\n/)) {
    const treffer = /^\s*@([a-z0-9-]+)\s*:\s*(.*)$/i.exec(roh);
    if (treffer) {
      einstellungen[treffer[1]!.toLowerCase()] = treffer[2]!.trim();
      continue;
    }
    zeilen.push(roh);
  }

  return { einstellungen, inhalt: zeilen.join('\n') };
}

export type Eintrag = {
  /** Die per `|` getrennten Felder, bereits getrimmt. Immer mindestens eines. */
  felder: string[];
};

export type Gruppe = {
  /** Leerer String, wenn die Eintraege vor der ersten `##`-Zeile stehen. */
  titel: string;
  eintraege: Eintrag[];
};

/** Erkennt Markdown-Trennzeilen wie `|---|---|` oder `| :--- | ---: |`. */
function istTrennzeile(zeile: string): boolean {
  return /^\|?\s*:?-{3,}:?\s*(\|\s*:?-{3,}:?\s*)*\|?$/.test(zeile);
}

/**
 * Zerlegt eine Zeile an `|` und behandelt `\|` als literales Zeichen.
 *
 * Bewusst eine Schleife statt eines Regex mit Lookbehind: Lookbehind laesst
 * aeltere Safari-Versionen schon beim Einlesen der Datei scheitern, und dann
 * faellt nicht nur diese Zeile aus, sondern die ganze Komponente.
 */
function felderVon(zeile: string): string[] {
  const felder: string[] = [];
  let aktuell = '';

  for (let i = 0; i < zeile.length; i++) {
    const zeichen = zeile[i]!;

    if (zeichen === '\\' && zeile[i + 1] === '|') {
      aktuell += '|';
      i++;
      continue;
    }

    if (zeichen === '|') {
      felder.push(aktuell.trim());
      aktuell = '';
      continue;
    }

    aktuell += zeichen;
  }
  felder.push(aktuell.trim());

  // Markdown-Randstriche ergeben vorne und hinten je ein leeres Feld. Nur die
  // entfernen — ein leeres Feld mitten in der Zeile ist eine echte leere Zelle.
  if (felder.length > 1 && zeile.startsWith('|')) felder.shift();
  if (felder.length > 1 && zeile.endsWith('|')) felder.pop();

  return felder;
}

/**
 * Zerlegt ein mehrzeiliges Textfeld in Gruppen und Eintraege.
 *
 * Eintraege vor der ersten `##`-Zeile landen in einer Gruppe mit leerem Titel.
 * Bausteine ohne Kategorien (Tabelle, Balken) ignorieren den Titel einfach.
 */
export function zerlegeGruppen(quelle: string | null | undefined): Gruppe[] {
  if (!quelle) return [];

  const gruppen: Gruppe[] = [];
  let aktuelle: Gruppe | undefined;

  for (const roh of quelle.split(/\r?\n/)) {
    const zeile = roh.trim();
    if (zeile === '' || istTrennzeile(zeile)) continue;

    const kategorie = /^#{1,6}\s+(.*)$/.exec(zeile);
    if (kategorie) {
      aktuelle = { titel: kategorie[1]!.trim(), eintraege: [] };
      gruppen.push(aktuelle);
      continue;
    }

    // Die erste Gruppe entsteht auch dann, wenn nie eine Ueberschrift kam.
    if (!aktuelle) {
      aktuelle = { titel: '', eintraege: [] };
      gruppen.push(aktuelle);
    }

    aktuelle.eintraege.push({ felder: felderVon(zeile) });
  }

  // Eine Ueberschrift ohne Eintraege darunter ist fast immer ein Tippfehler und
  // wuerde als leere Zwischenzeile erscheinen. Lieber still weglassen.
  return gruppen.filter((gruppe) => gruppe.eintraege.length > 0);
}

/** Alle Eintraege ohne Gruppierung — fuer Bausteine, die keine Kategorien kennen. */
export function zerlegeZeilen(quelle: string | null | undefined): Eintrag[] {
  return zerlegeGruppen(quelle).flatMap((gruppe) => gruppe.eintraege);
}

export type Tabelle = {
  kopf: string[];
  zeilen: string[][];
};

/**
 * Wie zerlegeZeilen, nur wird die erste Zeile als Kopfzeile gelesen und alle
 * Zeilen werden auf die Spaltenzahl des Kopfes gebracht.
 *
 * Das Auffuellen ist wichtig: eine Zeile mit einem Feld zu wenig wuerde sonst
 * die Tabelle verrutschen lassen, und im Designer sieht man dem Textfeld nicht
 * an, wo das fehlende `|` steckt.
 */
export function zerlegeTabelle(quelle: string | null | undefined): Tabelle | null {
  const zeilen = zerlegeZeilen(quelle);
  const [erste, ...rest] = zeilen;
  if (!erste) return null;

  const kopf = erste.felder;
  const spalten = kopf.length;

  return {
    kopf,
    zeilen: rest.map((zeile) => {
      const felder = zeile.felder.slice(0, spalten);
      while (felder.length < spalten) felder.push('');
      return felder;
    }),
  };
}

export type Wert = {
  /** null, wenn im Text keine Zahl steckt — dann wird nur `roh` angezeigt. */
  zahl: number | null;
  /** Was vor der Zahl steht, z. B. "ca. ". */
  praefix: string;
  /** Was nach der Zahl steht, z. B. " €" oder " %". */
  suffix: string;
  /** Nachkommastellen, wie sie im Text standen. */
  dezimalstellen: number;
  /** Der unveraenderte Text, als Rueckfallebene. */
  roh: string;
};

/**
 * Trennt eine Zahl aus einem Text wie "ca. 100.000 €" oder "5,75 % p.a."
 *
 * Gebraucht wird das fuers Hochzaehlen: animiert werden darf nur die Zahl,
 * Waehrungszeichen und Zusaetze muessen stehen bleiben. Deutsches Zahlenformat,
 * also Punkt als Tausendertrenner und Komma als Dezimaltrenner.
 *
 * Der Ziffernteil ist bewusst `\d+` und nicht `\d{1,3}`: mit der Dreiergrenze
 * haette "1234" nur als "123" gelesen, weil die Alternative fuer Zahlen ohne
 * Tausenderpunkt erst danach gegriffen haette.
 */
export function zerlegeWert(roh: string): Wert {
  const text = roh.trim();
  const muster = new RegExp(`-?\\d+(?:${TAUSENDERTRENNER}\\d{3})*(?:,\\d+)?`);
  const treffer = muster.exec(text);

  if (!treffer) {
    return { zahl: null, praefix: '', suffix: '', dezimalstellen: 0, roh: text };
  }

  const gefunden = treffer[0];
  const normalisiert = gefunden.replace(new RegExp(TAUSENDERTRENNER, 'g'), '').replace(',', '.');
  const zahl = Number(normalisiert);

  const komma = gefunden.indexOf(',');
  const dezimalstellen = komma === -1 ? 0 : gefunden.length - komma - 1;

  return {
    zahl: Number.isFinite(zahl) ? zahl : null,
    praefix: text.slice(0, treffer.index),
    suffix: text.slice(treffer.index + gefunden.length),
    dezimalstellen,
    roh: text,
  };
}

/** Deutsche Schreibweise, mit der Nachkommastellen-Zahl aus der Quelle. */
export function formatiere(zahl: number, dezimalstellen = 0): string {
  return new Intl.NumberFormat('de-DE', {
    minimumFractionDigits: dezimalstellen,
    maximumFractionDigits: dezimalstellen,
  }).format(zahl);
}
